import React from 'react'
import PropTypes from 'prop-types'
import { Button, Loader } from 'components/UIElements'
import { icons } from 'assets/assets'
import 'styles/d3/uploadinput.scss'

const NOT_IMAGE_TYPES = [
  'pdf',
  'doc',
  'CSV',
  'docx',
  'msword',
  'vnd.openxmlformats-officedocument.wordprocessingml.document'
]

class UploadInput extends React.Component {
  constructor(props) {
    super(props)
    this._getDefaultState = () => ({
      draggedOver: false,
      error: false,
      progress: null
    })
    this.state = this._getDefaultState()
  }

  onDragOver = e => {
    e.preventDefault()
  }

  onDragEnter = () => {
    this.setState({ draggedOver: true })
  }

  onDragLeave = e => {
    this.setState({ draggedOver: false })
  }

  onDrop = e => {
    e.preventDefault()
    if (e.dataTransfer.items && e.dataTransfer.items[0].kind === 'file') {
      this.handleFileLoad(e.dataTransfer.files[0])
    }
  }

  handleFileLoad = fileNode => {
    const { accept, onUpdateFile, imageValidations } = this.props
    const { name } = fileNode
    const extension = `.${name
      .split('.')
      .pop()
      .toLowerCase()}`
    if (accept && !accept.includes(extension)) {
      onUpdateFile({})
      this.setState({
        ...this._getDefaultState(),
        error:
          "Oh no! We can't take file types other than PDF, Word files, JPEG, or PNG."
      })
      onUpdateFile({})
    } else if (imageValidations) {
      const { size } = imageValidations
      onUpdateFile({})
      if (fileNode.size > size) {
        this.setState({
          ...this._getDefaultState(),
          error: `Your image exceeded 2.5 mb. Please reduce the file size.`
        })
      } else {
        onUpdateFile(fileNode)
        this.setState(this._getDefaultState())
      }
    } else {
      onUpdateFile({})
      onUpdateFile(fileNode)
      this.setState(this._getDefaultState())
    }
  }

  onClickToUpload = e => {
    e.preventDefault()
    this.fileInput.click()
  }

  onInputChange = e => {
    if (this.fileInput.files.length > 0) {
      this.handleFileLoad(this.fileInput.files[0])
    }
  }

  onProgress = e => {
    this.setState({ progress: Math.round((100 * e.loaded) / e.total) })
  }

  renderLoading = () => {
    const { strings } = this.props
    const { draggedOver } = this.state
    return (
      <div
        style={{ pointerEvents: draggedOver ? 'none' : '' }}
        className='input-container'>
        <Loader size={25} active />
        <div>{strings.uploading}</div>
      </div>
    )
  }

  renderInputContainer = () => {
    const { error } = this.state
    const { file, onUpdateFile, type, children, strings } = this.props
    return (
      <div className='input-container'>
        {file.name === undefined ? (
          <Button
            link
            className='clickable'
            onClick={this.onClickToUpload}
            type='button'>
            <div>
              {error && <p className='error'>{error}</p>}
              <div className='prompt'>
                <p>{strings.upload_instructions}</p>
                <p>{strings.or}</p>
                <div className='upload-button'>
                  {icons.upload}
                  {file.name ||
                    `${strings.upload || ''} ${type || strings.file || ''} `}
                </div>
              </div>
            </div>
          </Button>
        ) : (
          <div className='file flexed column'>
            <div className='flexed file-name'>
              <span>{file.name}</span>
              <i
                onClick={() => onUpdateFile({})}
                className='fas fa-times clickable'
              />
            </div>
            {file && !NOT_IMAGE_TYPES.includes(type) ? (
              <img
                src={`${window.URL.createObjectURL(file)}`}
                alt='uploading'
              />
            ) : (
              <i className='far fa-file-alt' />
            )}
          </div>
        )}
        {children}
      </div>
    )
  }

  render() {
    const { accept, loading, name, file } = this.props
    const { draggedOver, error } = this.state
    let className = 'upload-input'
    // const file = file || {}
    const hasFile = file.name !== undefined
    className += draggedOver ? ' dragged-over' : ''
    className += hasFile ? ' loaded' : ''
    className += error ? ' has-error' : ''
    className += loading ? ' loading' : ''
    return (
      <div
        onDrop={this.onDrop}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onDragOver={this.onDragOver}
        className={className}>
        <input
          name={name || ''}
          type='file'
          onChange={this.onInputChange}
          style={{ display: 'none' }}
          ref={el => {
            this.fileInput = el
          }}
          accept={accept instanceof Array ? accept.join(',') : undefined}
        />
        {loading ? this.renderLoading() : this.renderInputContainer()}
      </div>
    )
  }
}

UploadInput.propTypes = {
  accept: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  loading: PropTypes.bool,
  name: PropTypes.string,
  onUpdateFile: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  type: PropTypes.string
}

export default UploadInput
